import { useEffect, useRef } from 'react';

const supervisionItems = [
  {
    icon: 'ri-clipboard-line',
    title: '감리 계획 수립',
    desc: '공사 전 석면해체·제거 감리 계획서를 작성하고 작업 일정, 방법, 안전 기준을 사전 검토합니다.',
  },
  {
    icon: 'ri-eye-2-line',
    title: '작업 과정 감독',
    desc: '석면 작업 전 과정에 감리원이 상주하며 법적 기준 준수 여부를 실시간으로 확인합니다.',
  },
  {
    icon: 'ri-microscope-line',
    title: '잔류 석면 확인',
    desc: '해체·제거 완료 후 공기 중 석면 농도를 측정하여 잔류 석면 여부를 과학적으로 검증합니다.',
  },
  {
    icon: 'ri-file-check-line',
    title: '감리 완료 보고',
    desc: '모든 작업 과정과 측정 결과를 종합한 감리 완료 보고서를 발주처에 제출합니다.',
  },
];

const legalBasis = [
  { law: '석면안전관리법', content: '석면건축물 해체·제거 시 신고 및 감리 의무화' },
  { law: '산업안전보건법', content: '석면 작업 시 특수 건강진단, 보호구 착용 의무' },
  { law: '폐기물관리법', content: '석면 함유 폐기물의 지정 폐기물 처리 기준' },
  { law: '인력 기준', content: '800㎡ 이상 2000㎡ 미만: 일반감리원,\n2000㎡ 이상: 고급감리원 배치' },
];

export default function SupervisionSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.section-fade-in').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="supervision" className="py-24 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        {/* 메인 배너 이미지 — PC만 표시 */}
        <div className="relative section-fade-in rounded-3xl overflow-hidden mb-16 min-h-[400px] hidden md:block">
          <img
            src="https://public.readdy.ai/ai/img_res/164b0acdb4f97849f036f7c1383dbbcc.jpg"
            alt="석면 해체 제거 감리"
            className="w-full h-full object-cover object-top"
            style={{ minHeight: '400px' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-green-950/85 via-green-950/50 to-transparent flex items-center">
            <div className="p-10 lg:p-16 max-w-xl">
              <h3 className="text-3xl font-bold text-white mb-4 leading-tight">
                전문 감리로<br />시공 품질을 보증합니다
              </h3>
              <p className="text-white/70 text-[13.6px] leading-relaxed mb-8">
                석면안전관리법에 따라 일정 규모 이상의 석면해체·제거 작업은 반드시<br />감리를 받아야 합니다. 당사의 전문 감리원이 모든 과정을 철저히 검수하여<br />법적 적합성과 안전성을 보장합니다.
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-star-line text-white text-lg"></i>
                </div>
                <span className="text-white font-semibold">환경부 허가 감리 기관 등록</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          <div>
            <h3 className="text-xl font-bold text-green-950 mb-6 section-fade-in">감리 업무 절차</h3>
            <div className="flex flex-col gap-4">
              {supervisionItems.map((item, i) => (
                <div
                  key={item.title}
                  className="section-fade-in flex items-start gap-4 p-5 rounded-2xl border border-stone-100 hover:border-green-200 hover:bg-green-50/30 transition-all duration-200"
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-green-800 rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-white text-base`}></i>
                  </div>
                  <div>
                    <div className="font-semibold text-green-950 mb-1">{item.title}</div>
                    <p className="text-sm text-stone-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xl font-bold text-green-950 mb-6 section-fade-in">관련 법적 근거</h3>
            <div className="bg-stone-50 rounded-3xl p-6 border border-stone-100">
              <div className="grid grid-cols-2 gap-4">
                {legalBasis.map((item, i) => (
                  <div
                    key={item.law}
                    className="section-fade-in flex flex-col gap-3 p-4 bg-white rounded-2xl border border-stone-200 h-full"
                    style={{ transitionDelay: `${i * 100}ms` }}
                  >
                    <div className="px-3 py-1.5 bg-green-800 rounded-lg self-start">
                      <span className="text-white text-xs font-semibold whitespace-nowrap">{item.law}</span>
                    </div>
                    <p className="text-sm text-stone-600 leading-relaxed whitespace-pre-line">{item.content}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="section-fade-in mt-6 bg-amber-50 border border-amber-200 rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-amber-600 text-lg flex-shrink-0 mt-0.5"></i>
                <div>
                  <div className="font-semibold text-amber-800 text-sm mb-1">감리 의무 대상</div>
                  <p className="text-amber-700 text-xs leading-relaxed">
                    석면 함유 자재의 면적이 800m² 이상인 경우
                    석면해체·제거 작업 시 반드시 감리를 받아야 합니다.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
